import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import {
  AdminPageHeader,
  Button,
  ButtonStyles,
  DashboardPageHeaderMobile,
  Input,
  MLPVersion,
  TextArea,
  Theme,
  useBreakpoint,
} from '../../../../../ui/components';
import { LoggerService, PAGES, trackPage } from '../../../../../ui/utils';
import { useAdminToast } from '../../../../../ui/components/AdminToast/AdminToast';
import TeamService from '../../../../../sdk/services/teamService';
import { useAPICall } from '../../../../../hooks';
import type { ButtonTheme } from '../../../../../ui/components';
import { isValidEmail } from '../../../../../ui/utils/common';
import { TeamData } from '../../../../admin/teams/teamsListConfig';
import { initTeamForm } from '../../../../admin/teams';
import styles from './index.module.scss';

export const AddParticipantDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const apiCall = useAPICall();
  const viewPortType = useBreakpoint();
  const isDesktopView = ['xs', 'sm', 'md'].indexOf(viewPortType) === -1;
  const { adminToast } = useAdminToast();
  const [searchParams] = useSearchParams();
  const teamId = searchParams.get('');
  const [emailValid, setEmailValid] = useState(false);
  const [teamData, setTeamData] = useState<TeamData>(initTeamForm);
  const [email, setEmail] = useState('');

  const onSave = async () => {
    const payload = {
      campaignId: teamData.campaignId,
      teamId: teamData.teamId,
      email: email,
      teamName: teamData.teamName,
    };
    // try {
    const result = await apiCall(TeamService.teamInvite, payload);

    if (!result?.data?.result) {
      if (result.status !== 500) {
        adminToast({
          message: result?.data?.message,
          theme: Theme.ERROR,
        });
      }
    } else {
      navigate(-1);
      adminToast({
        message: t('Invite sent successfully'),
        theme: Theme.SUCCESS,
      });
    }
  };

  const buttonConfig = [
    {
      title: t('Send Invitation'),
      buttonStyle: ButtonStyles.SOLID,
      theme: Theme.BLACK as ButtonTheme,
      disabled: !emailValid,
      onClick: onSave,
    },
  ];

  const fetchTeamData = async () => {
    try {
      const result = await apiCall(TeamService.getTeamDetails, `${teamId}`);
      setTeamData(result?.data?.data ?? initTeamForm);
    } catch (error) {
      LoggerService.log('Failed to retrieve team detail', error);
    }
  };

  const handleInputChange = (email: string) => {
    setEmailValid(isValidEmail(email));
    setEmail(email);
  };

  useEffect(() => {
    fetchTeamData();

    const timeout = setTimeout(() => {
      trackPage(PAGES.addParticipant);
    }, 1000);
    return () => {
      clearTimeout(timeout);
    };
  }, []);

  return (
    <div className={styles['add-participant-main-container']}>
      {isDesktopView ? (
        <AdminPageHeader
          title={t('admin.addParticipant.title')}
          button={buttonConfig}
          withBackButton
          onBackButtonClick={() => {
            navigate(-1);
          }}
        />
      ) : (
        <DashboardPageHeaderMobile
          title={t('admin.addParticipant.title')}
          withBackButton
          onBackButtonClick={() => {
            navigate(-1);
          }}
        />
      )}
      <div className={styles['add-participant-form-container']}>
        <div className={styles['input-container']}>
          <div className={styles['pin-dropdown']}>
            <Input
              name={'campaignName'}
              placeholder={t('admin.addParticipant.campaignTitle')}
              value={teamData.campaignName}
              disabled
              mlpVersion={MLPVersion.ONE}
              label={t('admin.addParticipant.campaignTitle')}
            />
          </div>
          <div className={styles['team-name-section']}>
            <Input
              name={'teamName'}
              placeholder={t('admin.addParticipant.team')}
              value={teamData.teamName}
              disabled
              mlpVersion={MLPVersion.ONE}
              label={t('admin.addParticipant.team')}
            />
          </div>
        </div>
        <div className={styles['input-container']}>
          <TextArea
            mlpVersion={MLPVersion.ONE}
            label={t('admin.addParticipant.description')}
            placeholder={t('admin.addParticipant.description')}
            value={teamData?.description}
            disabled
          />
        </div>
      </div>
      <hr className={styles['horizontal-line']} />

      <div className={styles['add-participant-form-container']}>
        <div>
          <Input
            name={'email'}
            placeholder={t('john.doe@example.com')}
            value={email}
            mlpVersion={MLPVersion.ONE}
            message={!emailValid ? 'Please enter a valid email' : ''}
            theme={!emailValid ? Theme.ERROR : Theme.BLACK}
            label={t('Email Address')}
            onChange={e => {
              handleInputChange(e.target.value);
            }}
          />
        </div>
      </div>

      {!isDesktopView ? (
        <div className={styles['mobile-button-container']}>
          <Button
            title={t('Send Invitation')}
            buttonStyle={ButtonStyles.SOLID}
            disabled={!emailValid}
            onClick={onSave}
          />
        </div>
      ) : null}
    </div>
  );
};
